import './info-box.css';

/**
 * A dismissible inline info box with success / warning / error variants, styled
 * consistently with the rest of the app (and matching the LnE edit-warning look).
 *
 * Returns a detached element you can mount where you like. The box carries a
 * leading status icon, arbitrary body content, and an "×" dismiss button in the
 * corner that removes the box from the DOM (and fires the optional onDismiss).
 *
 * @param {object} opts
 * @param {'success'|'warning'|'error'|'info'} [opts.kind='success'] Visual variant.
 * @param {Node|string} opts.content Body content (a node, or plain text).
 * @param {boolean} [opts.dismissible=true] Whether to render the "×" dismiss
 *   button. Set false for boxes that carry their own actions (e.g. a review
 *   prompt with Save/Cancel) where a dismiss would be ambiguous.
 * @param {() => void} [opts.onDismiss] Called after the box is removed.
 * @returns {HTMLDivElement}
 */
export function createInfoBox({ kind = 'success', content, dismissible = true, onDismiss } = {}) {
  const box = document.createElement('div');
  box.className = 'info-box';
  box.dataset.kind = kind;
  box.setAttribute('role', kind === 'error' ? 'alert' : 'status');

  const icon = document.createElement('i');
  icon.className = `info-box-icon fa-solid ${ICONS[kind] ?? ICONS.success}`;
  icon.setAttribute('aria-hidden', 'true');

  const body = document.createElement('div');
  body.className = 'info-box-body';
  if (typeof content === 'string') body.textContent = content;
  else if (content) body.appendChild(content);

  box.append(icon, body);

  if (dismissible) {
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'info-box-dismiss icon-button';
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.title = 'Dismiss';
    dismiss.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
    dismiss.addEventListener('click', () => {
      box.remove();
      onDismiss?.();
    });
    box.appendChild(dismiss);
  }

  return box;
}

const ICONS = {
  success: 'fa-circle-check',
  warning: 'fa-triangle-exclamation',
  error: 'fa-circle-exclamation',
  info: 'fa-circle-info',
};

/**
 * Show (or clear) a single info-box inside a host element — the common
 * "one message at a time" pattern used by dialogs/forms. Replaces whatever the
 * host currently holds; a falsy `message` clears it.
 *
 * @param {HTMLElement} host  the mount element (e.g. a `<p class="…__error">`)
 * @param {'success'|'warning'|'error'|'info'} kind  the variant
 * @param {Node|string} [message]  the body; falsy clears the host
 * @param {object} [opts]  extra createInfoBox options (e.g. { dismissible })
 */
export function setInfoBox(host, kind, message, opts = {}) {
  if (!host) return;
  if (!message) {
    host.replaceChildren();
    return;
  }
  host.replaceChildren(createInfoBox({ kind, content: message, ...opts }));
}
